'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import api from '../../../domain/api';
import { useSelectAllFacebookAccount } from '../../../shared/hooks/UseFacebookAccount';

const META_CAMPAIGN_COLUMNS_STORAGE_KEY = 'meta-campaign-visible-columns-v1';

const DATE_PRESETS = [
  { label: 'Yesterday', value: 'yesterday' },
  { label: '7 Days', value: 'last_7_days' },
  { label: '14 Days', value: 'last_14_days' },
  { label: '30 Days', value: 'last_30_days' },
  { label: 'Custom Range', value: 'custom' },
];

const ALL_COLUMNS = [
  { id: 'campaign_name', label: 'Campaign' },
  { id: 'campaign_id', label: 'Campaign ID' },
  { id: 'campaign_status', label: 'Status' },
  { id: 'objective', label: 'Objective' },
  { id: 'start_time', label: 'Start Date' },
  { id: 'updated_time', label: 'Updated' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'reach', label: 'Reach' },
  { id: 'frequency', label: 'Frequency' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'link_clicks', label: 'Link Clicks' },
  { id: 'outbound_clicks', label: 'Outbound Clicks' },
  { id: 'landing_page_views', label: 'LP Views' },
  { id: 'ctr', label: 'CTR (%)' },
  { id: 'cpc', label: 'CPC' },
  { id: 'cpm', label: 'CPM' },
  { id: 'spend', label: 'Spend' },
  { id: 'leads', label: 'Leads' },
  { id: 'conversion_rate', label: 'Conversion Rate' },
  { id: 'lead_rate_per_1000_impressions', label: 'Lead Rate / 1K' },
  { id: 'cost_per_lead', label: 'Cost Per Lead' },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.map((column) => column.id);

const readVisibleColumns = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  try {
    const rawValue = window.localStorage.getItem(META_CAMPAIGN_COLUMNS_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : DEFAULT_VISIBLE_COLUMNS;
    const validColumnIds = new Set(ALL_COLUMNS.map((column) => column.id));

    if (!Array.isArray(parsed) || parsed.length === 0) {
      return DEFAULT_VISIBLE_COLUMNS;
    }

    const filteredColumns = parsed.filter((columnId) => validColumnIds.has(columnId));
    return filteredColumns.length > 0 ? filteredColumns : DEFAULT_VISIBLE_COLUMNS;
  } catch {
    return DEFAULT_VISIBLE_COLUMNS;
  }
};

const readInitialState = (search) => {
  const params = new URLSearchParams(search);

  return {
    accountId: params.get('accountId') || '',
    sorting: {
      id: params.get('sortBy') || 'spend',
      desc: (params.get('sortOrder') || 'desc') === 'desc',
    },
    pagination: {
      pageIndex: Math.max(Number.parseInt(params.get('page'), 10) - 1 || 0, 0),
      pageSize: Number.parseInt(params.get('limit'), 10) || 15,
    },
    dateFilter: {
      rangeType: params.get('rangeType') || 'yesterday',
      from: params.get('from') || '',
      to: params.get('to') || '',
    },
  };
};

const fetchReports = async ({ queryKey }) => {
  const [, filters] = queryKey;
  const res = await api.post('/facebook-ads/campaign-insights', filters);
  return res.data;
};

const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return 'N/A';
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return Number(amount).toFixed(2);
  }
};

const formatInteger = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString();
};

const formatPercentage = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0.00%';
  }

  return `${number.toFixed(2)}%`;
};

const formatDecimal = (value, digits = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0.00';
  }

  return number.toFixed(digits);
};

const formatObjective = (value) => {
  if (!value) {
    return 'N/A';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateValue = (value) => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const EnhancedFacebookAdsTable = () => {
  const location = useLocation();
  const history = useHistory();
  const columnMenuRef = useRef(null);
  const initialState = useMemo(() => readInitialState(location.search), [location.search]);

  const [account, setAccount] = useState(initialState.accountId);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [dateFilter, setDateFilter] = useState(initialState.dateFilter);
  const [appliedDateFilter, setAppliedDateFilter] = useState(
    initialState.dateFilter,
  );
  const [visibleColumns, setVisibleColumns] = useState(readVisibleColumns);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  const [accountData, isAccountsLoading] = useSelectAllFacebookAccount();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      META_CAMPAIGN_COLUMNS_STORAGE_KEY,
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setIsColumnMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const nextState = readInitialState(location.search);

    setAccount((current) =>
      current === nextState.accountId ? current : nextState.accountId,
    );
    setSorting((current) =>
      current.id === nextState.sorting.id && current.desc === nextState.sorting.desc
        ? current
        : nextState.sorting,
    );
    setPagination((current) =>
      current.pageIndex === nextState.pagination.pageIndex &&
      current.pageSize === nextState.pagination.pageSize
        ? current
        : nextState.pagination,
    );
    setDateFilter((current) =>
      current.rangeType === nextState.dateFilter.rangeType &&
      current.from === nextState.dateFilter.from &&
      current.to === nextState.dateFilter.to
        ? current
        : nextState.dateFilter,
    );
    setAppliedDateFilter((current) =>
      current.rangeType === nextState.dateFilter.rangeType &&
      current.from === nextState.dateFilter.from &&
      current.to === nextState.dateFilter.to
        ? current
        : nextState.dateFilter,
    );
  }, [location.search]);

  useEffect(() => {
    if (appliedDateFilter.rangeType === 'custom') {
      return;
    }

    const params = new URLSearchParams(location.search);

    if (account) {
      params.set('accountId', account);
    } else {
      params.delete('accountId');
    }

    params.set('tab', 'campaigns');
    params.set('rangeType', appliedDateFilter.rangeType);
    params.delete('from');
    params.delete('to');
    params.set('sortBy', sorting.id);
    params.set('sortOrder', sorting.desc ? 'desc' : 'asc');
    params.set('page', String(pagination.pageIndex + 1));
    params.set('limit', String(pagination.pageSize));

    const nextSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  }, [
    account,
    appliedDateFilter.rangeType,
    history,
    location.pathname,
    location.search,
    pagination.pageIndex,
    pagination.pageSize,
    sorting.desc,
    sorting.id,
  ]);

  const accountOptions = useMemo(() => {
    return (accountData?.adAccounts || []).map((adAccount) => ({
      label: adAccount.accountName,
      value: adAccount.accountId,
    }));
  }, [accountData]);

  const selectedAccount = useMemo(() => {
    return (accountData?.adAccounts || []).find(
      (adAccount) => String(adAccount.accountId) === String(account),
    );
  }, [account, accountData]);

  const queryPayload = useMemo(
    () => ({
      accountId: account,
      sortBy: sorting.id,
      sortOrder: sorting.desc ? 'desc' : 'asc',
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      rangeType: appliedDateFilter.rangeType,
      from: appliedDateFilter.from,
      to: appliedDateFilter.to,
    }),
    [account, appliedDateFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['facebook-ads-reports', queryPayload],
    queryFn: fetchReports,
    enabled: Boolean(
      account &&
        (appliedDateFilter.rangeType !== 'custom' ||
          (appliedDateFilter.from && appliedDateFilter.to)),
    ),
  });

  useEffect(() => {
    setPagination((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    );
  }, [account, appliedDateFilter.rangeType, appliedDateFilter.from, appliedDateFilter.to]);

  const handleSort = (columnId) => {
    setSorting((current) => ({
      id: columnId,
      desc: current.id === columnId ? !current.desc : false,
    }));
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const applyCustomDateFilter = () => {
    if (!dateFilter.from || !dateFilter.to) {
      return;
    }

    const nextFilter = {
      rangeType: 'custom',
      from: dateFilter.from,
      to: dateFilter.to,
    };

    setAppliedDateFilter(nextFilter);
    setPagination((current) => ({ ...current, pageIndex: 0 }));

    const params = new URLSearchParams(location.search);
    params.set('tab', 'campaigns');
    params.set('accountId', account);
    params.set('rangeType', 'custom');
    params.set('from', dateFilter.from);
    params.set('to', dateFilter.to);
    params.set('sortBy', sorting.id);
    params.set('sortOrder', sorting.desc ? 'desc' : 'asc');
    params.set('page', '1');
    params.set('limit', String(pagination.pageSize));

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const rows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 0;
  const visibleTableColumns = ALL_COLUMNS.filter((column) =>
    visibleColumns.includes(column.id),
  );

  const renderCell = (columnId, row) => {
    const currency = selectedAccount?.currency || 'INR';

    switch (columnId) {
      case 'campaign_name':
        return (
          <div className='campaign-cell'>
            <span className='campaign-name'>{row.campaign_name || 'Unnamed campaign'}</span>
          </div>
        );
      case 'campaign_id':
        return <span className='mono-cell'>{row.campaign_id || 'N/A'}</span>;
      case 'campaign_status':
        return (
          <span
            className={`status-pill ${(row.campaign_status || 'unknown')
              .toLowerCase()
              .replace(/[_\s]+/g, '-')}`}
          >
            <i className='fa fa-circle' aria-hidden='true'></i>
            {formatObjective(row.campaign_status)}
          </span>
        );
      case 'objective':
        return formatObjective(row.objective);
      case 'start_time':
      case 'updated_time':
        return formatDateValue(row[columnId]);
      case 'impressions':
      case 'reach':
      case 'clicks':
      case 'link_clicks':
      case 'outbound_clicks':
      case 'landing_page_views':
      case 'leads':
        return formatInteger(row[columnId]);
      case 'frequency':
        return formatDecimal(row.frequency);
      case 'ctr':
      case 'conversion_rate':
        return row[columnId] === null ? 'N/A' : formatPercentage(row[columnId]);
      case 'lead_rate_per_1000_impressions':
        return row.lead_rate_per_1000_impressions === null
          ? 'N/A'
          : formatDecimal(row.lead_rate_per_1000_impressions);
      case 'cpc':
      case 'cpm':
      case 'spend':
        return row[columnId] === null
          ? 'N/A'
          : formatCurrency(row[columnId], currency);
      case 'cost_per_lead':
        return row.cost_per_lead === null
          ? 'N/A'
          : formatCurrency(row.cost_per_lead, currency);
      default:
        return row[columnId] ?? 'N/A';
    }
  };

  const toggleColumn = (columnId) => {
    setVisibleColumns((current) => {
      if (current.includes(columnId)) {
        const nextColumns = current.filter((id) => id !== columnId);
        return nextColumns.length > 0 ? nextColumns : current;
      }

      const selectedColumns = new Set([...current, columnId]);
      return ALL_COLUMNS.map((column) => column.id).filter((id) =>
        selectedColumns.has(id),
      );
    });
  };

  const resetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  const createPageNumbers = () => {
    if (!totalPages) {
      return [];
    }

    const start = Math.max(0, pagination.pageIndex - 2);
    return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
      const pageIndex = start + index;
      return pageIndex < totalPages ? pageIndex : null;
    }).filter((pageIndex) => pageIndex !== null);
  };

  return (
    <div className='meta-report-shell'>
      <div className='meta-report-header'>
        <div>
          <h1 className='meta-report-title'>
            <i className='fa fa-facebook-square' aria-hidden='true'></i>
            Meta Campaigns
          </h1>
          <p className='meta-report-subtitle'>
            Campaign performance pulled directly from the Meta Ads API.
          </p>
        </div>

        <div className='selector-panel'>
          <label className='selector-label'>Ad Account</label>
          <Select
            className='react-select-container'
            classNamePrefix='react-select'
            options={accountOptions}
            value={accountOptions.find((option) => option.value === account) || null}
            onChange={(selected) => {
              setAccount(selected ? selected.value : '');
              setPagination((current) => ({ ...current, pageIndex: 0 }));
            }}
            placeholder='Select Meta ad account'
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            isLoading={isAccountsLoading}
            isClearable
          />
        </div>
      </div>

      {selectedAccount && (
        <div className='account-summary-card'>
          <div className='summary-primary'>
            <div>
              <div className='summary-eyebrow'>Selected Account</div>
              <h2>{selectedAccount.accountName}</h2>
            </div>
            <span
              className={`status-pill ${selectedAccount.status?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}
            >
              <i className='fa fa-circle' aria-hidden='true'></i>
              {selectedAccount.status || 'Unknown'}
            </span>
          </div>

          <div className='summary-grid'>
            <div className='summary-item'>
              <span className='summary-label'>Account ID</span>
              <span className='summary-value mono-cell'>
                {selectedAccount.accountId}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Business</span>
              <span className='summary-value'>
                {selectedAccount.businessName || 'N/A'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Currency</span>
              <span className='summary-value'>{selectedAccount.currency || 'N/A'}</span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Time Zone</span>
              <span className='summary-value'>
                {selectedAccount.timezoneName || 'N/A'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Funding</span>
              <span className='summary-value'>
                {selectedAccount.paymentType || 'Unknown'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Available Funds</span>
              <span className='summary-value'>
                {selectedAccount.availableFundsLabel ||
                  selectedAccount.availableBalanceLabel ||
                  'N/A'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Amount Spent</span>
              <span className='summary-value'>
                {selectedAccount.amountSpentLabel || 'N/A'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Spend Cap</span>
              <span className='summary-value'>
                {selectedAccount.spendCapLabel || 'N/A'}
              </span>
            </div>
            <div className='summary-item'>
              <span className='summary-label'>Current Balance</span>
              <span className='summary-value'>
                {selectedAccount.currentBalanceLabel || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {account && (
        <div className='report-filters-card'>
          <div className='date-filter-row'>
            <div className='filters-topbar'>
              <div className='date-filter-label'>
                <i className='fa fa-calendar' aria-hidden='true'></i>
                Date Range
              </div>

              <div className='column-toggle-wrap' ref={columnMenuRef}>
                <button
                  type='button'
                  className='secondary-button'
                  onClick={() => setIsColumnMenuOpen((current) => !current)}
                >
                  <i className='fa fa-columns' aria-hidden='true'></i>
                  Columns
                </button>

                {isColumnMenuOpen && (
                  <div className='column-menu'>
                    <div className='column-menu-header'>
                      <strong>Visible Columns</strong>
                      <button
                        type='button'
                        className='column-reset-button'
                        onClick={resetColumns}
                      >
                        Reset
                      </button>
                    </div>

                    <div className='column-menu-grid'>
                      {ALL_COLUMNS.map((column) => (
                        <label key={column.id} className='column-option'>
                          <input
                            type='checkbox'
                            checked={visibleColumns.includes(column.id)}
                            onChange={() => toggleColumn(column.id)}
                          />
                          <span>{column.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='preset-row'>
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type='button'
                  className={`preset-button ${
                    dateFilter.rangeType === preset.value ? 'active' : ''
                  }`}
                  onClick={() => {
                    if (preset.value === 'custom') {
                      setDateFilter((current) => ({
                        ...current,
                        rangeType: 'custom',
                      }));
                      return;
                    }

                    const nextFilter = {
                      rangeType: preset.value,
                      from: '',
                      to: '',
                    };

                    setDateFilter(nextFilter);
                    setAppliedDateFilter(nextFilter);
                    setPagination((current) => ({ ...current, pageIndex: 0 }));
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {dateFilter.rangeType === 'custom' && (
              <div className='custom-date-card'>
                <div className='custom-date-field'>
                  <label>From</label>
                  <input
                    type='date'
                    value={dateFilter.from}
                    onChange={(event) =>
                      setDateFilter((current) => ({
                        ...current,
                        from: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className='custom-date-field'>
                  <label>To</label>
                  <input
                    type='date'
                    value={dateFilter.to}
                    onChange={(event) =>
                      setDateFilter((current) => ({
                        ...current,
                        to: event.target.value,
                      }))
                    }
                  />
                </div>
                <button
                  type='button'
                  className='primary-button'
                  onClick={applyCustomDateFilter}
                  disabled={!dateFilter.from || !dateFilter.to}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className='report-table-card'>
        {!account ? (
          <div className='report-feedback'>
            <i className='fa fa-hand-pointer-o' aria-hidden='true'></i>
            <p>Select a Meta ad account to load campaigns.</p>
          </div>
        ) : isLoading ? (
          <div className='report-feedback'>
            <div className='loading-spinner'></div>
            <p>Loading campaigns from Meta...</p>
          </div>
        ) : isError ? (
          <div className='report-feedback'>
            <i className='fa fa-exclamation-triangle' aria-hidden='true'></i>
            <p>{error?.message || 'Failed to fetch campaign insights'}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className='report-feedback'>
            <i className='fa fa-search' aria-hidden='true'></i>
            <p>No campaigns were returned for the selected account and date range.</p>
          </div>
        ) : (
          <>
            {isFetching && (
              <div className='refresh-banner'>
                <div className='loading-spinner small'></div>
                Refreshing campaign data
              </div>
            )}

            <div className='table-scroll'>
              <table className='report-table'>
                <thead>
                  <tr>
                    {visibleTableColumns.map((column) => (
                      <th key={column.id}>
                        <button
                          type='button'
                          className='sort-button'
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                          {sorting.id === column.id && (
                            <i
                              className={`fa ${
                                sorting.desc ? 'fa-caret-down' : 'fa-caret-up'
                              }`}
                              aria-hidden='true'
                            ></i>
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.campaign_id || row.campaign_name}>
                      {visibleTableColumns.map((column) => (
                        <td key={column.id}>{renderCell(column.id, row)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='pagination-row'>
              <span className='pagination-text'>
                Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
                {Math.min((pagination.pageIndex + 1) * pagination.pageSize, total)} of{' '}
                {total} campaigns
              </span>

              <div className='pagination-controls'>
                <button
                  type='button'
                  className='page-button'
                  onClick={() =>
                    setPagination((current) => ({ ...current, pageIndex: 0 }))
                  }
                  disabled={pagination.pageIndex === 0}
                >
                  <i className='fa fa-angle-double-left' aria-hidden='true'></i>
                </button>
                <button
                  type='button'
                  className='page-button'
                  onClick={() =>
                    setPagination((current) => ({
                      ...current,
                      pageIndex: Math.max(current.pageIndex - 1, 0),
                    }))
                  }
                  disabled={pagination.pageIndex === 0}
                >
                  <i className='fa fa-angle-left' aria-hidden='true'></i>
                </button>

                {createPageNumbers().map((pageIndex) => (
                  <button
                    key={pageIndex}
                    type='button'
                    className={`page-button ${
                      pageIndex === pagination.pageIndex ? 'active' : ''
                    }`}
                    onClick={() =>
                      setPagination((current) => ({ ...current, pageIndex }))
                    }
                  >
                    {pageIndex + 1}
                  </button>
                ))}

                <button
                  type='button'
                  className='page-button'
                  onClick={() =>
                    setPagination((current) => ({
                      ...current,
                      pageIndex: Math.min(current.pageIndex + 1, totalPages - 1),
                    }))
                  }
                  disabled={pagination.pageIndex >= totalPages - 1}
                >
                  <i className='fa fa-angle-right' aria-hidden='true'></i>
                </button>
                <button
                  type='button'
                  className='page-button'
                  onClick={() =>
                    setPagination((current) => ({
                      ...current,
                      pageIndex: Math.max(totalPages - 1, 0),
                    }))
                  }
                  disabled={pagination.pageIndex >= totalPages - 1}
                >
                  <i className='fa fa-angle-double-right' aria-hidden='true'></i>
                </button>

                <select
                  className='page-size-select'
                  value={pagination.pageSize}
                  onChange={(event) =>
                    setPagination({
                      pageIndex: 0,
                      pageSize: Number(event.target.value),
                    })
                  }
                >
                  {[10, 15, 25, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .meta-report-shell {
          background: #f5f7fb;
          min-height: 100vh;
        }

        .meta-report-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          padding: 18px 22px;
          background: linear-gradient(135deg, #1877f2 0%, #155fbe 100%);
          color: #fff;
          border-radius: 14px;
          margin-bottom: 18px;
        }

        .meta-report-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0;
          font-size: 22px;
          font-weight: 700;
        }

        .meta-report-subtitle {
          margin: 6px 0 0;
          color: rgba(255, 255, 255, 0.82);
          font-size: 13px;
        }

        .selector-panel {
          min-width: 280px;
          max-width: 360px;
          width: 100%;
        }

        .selector-label {
          display: block;
          margin-bottom: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.8);
        }

        .react-select-container :global(.react-select__control) {
          min-height: 42px;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: rgba(255, 255, 255, 0.1);
        }

        .react-select-container :global(.react-select__single-value),
        .react-select-container :global(.react-select__placeholder) {
          color: #fff;
        }

        .account-summary-card,
        .report-filters-card,
        .report-table-card {
          background: #fff;
          border: 1px solid #e5eaf3;
          border-radius: 14px;
          box-shadow: 0 8px 30px rgba(15, 23, 42, 0.05);
        }

        .account-summary-card {
          padding: 18px 20px;
          margin-bottom: 18px;
        }

        .summary-primary {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
        }

        .summary-eyebrow {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
          margin-bottom: 6px;
        }

        .summary-primary h2 {
          margin: 0;
          color: #0f172a;
          font-size: 20px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 14px;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px 14px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #edf2f7;
        }

        .summary-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
        }

        .summary-value {
          color: #0f172a;
          font-size: 14px;
          font-weight: 600;
        }

        .report-filters-card {
          padding: 18px 20px;
          margin-bottom: 18px;
        }

        .date-filter-row {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .filters-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .date-filter-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #475569;
        }

        .preset-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .preset-button,
        .primary-button,
        .secondary-button,
        .page-button {
          border: 1px solid #cfd7e6;
          background: #fff;
          color: #334155;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
        }

        .preset-button {
          height: 38px;
          padding: 0 14px;
        }

        .preset-button.active,
        .primary-button,
        .page-button.active {
          background: #1877f2;
          border-color: #1877f2;
          color: #fff;
        }

        .secondary-button {
          height: 38px;
          padding: 0 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .column-toggle-wrap {
          position: relative;
        }

        .column-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 320px;
          max-height: 420px;
          overflow-y: auto;
          padding: 14px;
          border-radius: 14px;
          border: 1px solid #e5eaf3;
          background: #ffffff;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
          z-index: 20;
        }

        .column-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
          color: #0f172a;
          font-size: 14px;
        }

        .column-reset-button {
          border: none;
          background: none;
          padding: 0;
          color: #1663d6;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
        }

        .column-menu-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 14px;
        }

        .column-option {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #334155;
          font-size: 13px;
          font-weight: 600;
        }

        .column-option input {
          width: 16px;
          height: 16px;
          accent-color: #1877f2;
        }

        .custom-date-card {
          display: flex;
          flex-wrap: wrap;
          align-items: end;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #edf2f7;
        }

        .custom-date-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .custom-date-field label {
          font-size: 12px;
          font-weight: 700;
          color: #475569;
        }

        .custom-date-field input,
        .page-size-select {
          height: 38px;
          border: 1px solid #cfd7e6;
          border-radius: 10px;
          padding: 0 12px;
          background: #fff;
        }

        .primary-button {
          height: 38px;
          padding: 0 16px;
        }

        .report-feedback {
          min-height: 280px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #5b6474;
        }

        .loading-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #dbe5f3;
          border-top-color: #1877f2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-spinner.small {
          width: 18px;
          height: 18px;
          border-width: 2px;
        }

        .refresh-banner {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin: 16px 16px 0;
          padding: 10px 12px;
          border-radius: 10px;
          background: #eff6ff;
          color: #1d4ed8;
          font-size: 13px;
          font-weight: 600;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .report-table {
          width: 100%;
          min-width: 1880px;
          border-collapse: collapse;
        }

        .report-table thead th {
          padding: 14px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e5eaf3;
          text-align: left;
        }

        .sort-button {
          border: none;
          background: none;
          padding: 0;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #5b6474;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
        }

        .report-table tbody td {
          padding: 15px 16px;
          border-bottom: 1px solid #eef2f7;
          color: #1f2937;
          font-size: 14px;
          vertical-align: middle;
        }

        .report-table tbody tr:hover {
          background: #f8fbff;
        }

        .campaign-cell {
          max-width: 320px;
        }

        .campaign-name {
          display: block;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.4;
        }

        .mono-cell {
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo,
            monospace;
          color: #556277;
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          background: #eef2f7;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
        }

        .status-pill.active {
          background: #eaf8ef;
          color: #16753b;
        }

        .status-pill.paused,
        .status-pill.campaign-paused,
        .status-pill.with-issues {
          background: #fff4dc;
          color: #b45309;
        }

        .status-pill.disabled,
        .status-pill.closed,
        .status-pill.rejected,
        .status-pill.archived,
        .status-pill.deleted {
          background: #fdecec;
          color: #b42318;
        }

        .pagination-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px;
        }

        .pagination-text {
          font-size: 13px;
          color: #556277;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-button {
          min-width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .page-button:disabled,
        .primary-button:disabled,
        .secondary-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .meta-report-header,
          .summary-primary,
          .filters-topbar,
          .pagination-row {
            flex-direction: column;
            align-items: stretch;
          }

          .selector-panel {
            max-width: none;
          }

          .column-menu {
            left: 0;
            right: auto;
            width: min(320px, calc(100vw - 64px));
          }

          .column-menu-grid {
            grid-template-columns: 1fr;
          }

          .pagination-controls {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
};

export default EnhancedFacebookAdsTable;
